// src/features/analytics/pages/StaffReportPage.tsx — 23-oct-2026
// ============================================================================
// Reporte «POR EMPLEADO» (pedido Hogar China City t1000080; Carlos: para todos los negocios de lealtad).
// Por empleado y día local del negocio: clientes nuevos, compras/visitas y monto, puntos/cashback otorgados,
// canjes, ajustes ± y cancelaciones. Los datos los arma el servidor (GET /loyalty/staff-report,
// lib/staffReport.js), con las mismas reglas que el Excel de movimientos para que cuadren.
// Dueño o gerente. PRO (va con «tu equipo»): un negocio gratis ve SOLO lo suyo + el candado.
// ============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FileSpreadsheet, Loader2, MapPin, UsersRound, Info } from 'lucide-react';
import Layout from '../../../components/layout/Layout';
import { useAuth } from '../../../context/AuthContext';
import { ProLocked } from '../../../components/pro/ProLocked';
import { staffReportApi, type StaffMetrics, type StaffReport } from '../../../api/analyticsApi';

const ymd = (d: Date, tz: string) => {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d); } catch { return d.toISOString().slice(0, 10); }
};
const sumaDias = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const fmtDia = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const s = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-MX', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const n0 = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 0 });
const pesos = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StaffReportPage: React.FC = () => {
  const { currentUser } = useAuth();
  const tz = currentUser?.timeZone || 'America/Mexico_City';
  const branches = (currentUser?.branches || []).filter((b) => b.isActive !== false);
  const puedeVer = !currentUser?.baseRole || ['owner', 'manager'].includes(currentUser.baseRole);

  const hoy = useMemo(() => ymd(new Date(), tz), [tz]);
  const atajos = useMemo(() => {
    const ini = `${hoy.slice(0, 8)}01`;
    const finPasado = sumaDias(ini, -1);
    return [
      { k: 'hoy', label: 'Hoy', from: hoy, to: hoy },
      { k: 'ayer', label: 'Ayer', from: sumaDias(hoy, -1), to: sumaDias(hoy, -1) },
      { k: '7', label: 'Últimos 7 días', from: sumaDias(hoy, -6), to: hoy },
      { k: 'mes', label: 'Este mes', from: ini, to: hoy },
      { k: 'pasado', label: 'Mes pasado', from: `${finPasado.slice(0, 8)}01`, to: finPasado },
    ];
  }, [hoy]);
  const [from, setFrom] = useState(atajos[2].from);
  const [to, setTo] = useState(atajos[2].to);
  const [branchId, setBranchId] = useState('');
  const [rep, setRep] = useState<StaffReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [abiertos, setAbiertos] = useState<Record<string, boolean>>({});
  const [bajando, setBajando] = useState(false);

  const cargar = useCallback(async () => {
    if (!from || !to || !puedeVer) return;
    setLoading(true); setError('');
    try {
      const r = await staffReportApi.get({ from, to, branchId: branchId || undefined });
      setRep(r);
      setAbiertos(r.empleados.length === 1 ? { [r.empleados[0].key]: true } : {});
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar el reporte');
    } finally { setLoading(false); }
  }, [from, to, branchId, puedeVer]);
  useEffect(() => { cargar(); }, [cargar]);

  const descargar = async () => {
    setBajando(true); setError('');
    try { await staffReportApi.excel({ from, to, branchId: branchId || undefined }); }
    catch (e: any) { setError(e?.message || 'No se pudo generar el Excel'); }
    finally { setBajando(false); }
  };

  const cb = rep ? rep.esCashback : currentUser?.loyaltyMode === 'cashback';
  const pts = (n: number) => (cb ? pesos(n) : `${n0(n)} pts`);
  const lVisitas = cb ? 'Compras' : 'Visitas';
  const lOtorgado = cb ? 'Cashback dado' : 'Puntos dados';
  const ajustes = (m: StaffMetrics) => {
    const p: string[] = [];
    if (m.ajustesMas) p.push(`+${m.ajustesMas} (${pts(m.ptsMas)})`);
    if (m.ajustesMenos) p.push(`−${m.ajustesMenos} (${pts(m.ptsMenos)})`);
    return p.length ? p.join(' · ') : '—';
  };
  const input = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-accent/50';

  // Celdas de una fila (empleado o día) en la tabla de escritorio.
  const celdas = (m: StaffMetrics) => (
    <>
      <td className="px-3 py-2.5 text-right tabular-nums">{m.nuevos ? n0(m.nuevos) : '—'}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{m.visitas ? n0(m.visitas) : '—'}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{m.monto ? pesos(m.monto) : '—'}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{m.otorgados ? pts(m.otorgados) : '—'}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{m.canjes ? `${n0(m.canjes)} · ${pts(m.canjeados)}` : '—'}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-xs">{ajustes(m)}</td>
      <td className={`px-3 py-2.5 text-right tabular-nums ${m.cancelaciones ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>{m.cancelaciones ? n0(m.cancelaciones) : '—'}</td>
    </>
  );
  // Rejilla de métricas para las tarjetas del móvil.
  const rejilla = (m: StaffMetrics) => (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
      {[
        ['Clientes nuevos', m.nuevos ? n0(m.nuevos) : '—'],
        [lVisitas, m.visitas ? `${n0(m.visitas)} · ${pesos(m.monto)}` : '—'],
        [lOtorgado, m.otorgados ? pts(m.otorgados) : '—'],
        ['Canjes', m.canjes ? `${n0(m.canjes)} · ${pts(m.canjeados)}` : '—'],
        ['Ajustes', ajustes(m)],
        ['Cancelaciones', m.cancelaciones ? n0(m.cancelaciones) : '—'],
      ].map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] text-content-3">{k}</dt>
          <dd className={`font-medium text-content tabular-nums break-words ${k === 'Cancelaciones' && m.cancelaciones ? 'text-red-600 dark:text-red-400' : ''}`}>{v}</dd>
        </div>
      ))}
    </dl>
  );

  const tiles = rep ? [
    { k: 'Clientes nuevos', v: n0(rep.totales.nuevos) },
    { k: lVisitas, v: n0(rep.totales.visitas), sub: rep.totales.monto ? pesos(rep.totales.monto) : undefined },
    { k: lOtorgado, v: pts(rep.totales.otorgados) },
    { k: 'Canjes', v: n0(rep.totales.canjes), sub: rep.totales.canjeados ? pts(rep.totales.canjeados) : undefined },
    { k: 'Ajustes', v: n0(rep.totales.ajustesMas + rep.totales.ajustesMenos) },
    { k: 'Cancelaciones', v: n0(rep.totales.cancelaciones) },
  ] : [];

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0" data-testid="staff-report">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <UsersRound className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" /> Por empleado
            </h1>
            <p className="mt-1 text-sm text-content-2">Clientes nuevos, compras, canjes y ajustes que registró cada persona, día por día.</p>
          </div>
          {puedeVer && rep?.pro && (
            <button type="button" onClick={descargar} disabled={bajando || loading} data-testid="staff-excel"
              className="inline-flex items-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:opacity-60">
              {bajando ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />} Descargar Excel
            </button>
          )}
        </div>

        {!puedeVer ? (
          <div className="bg-surface shadow rounded-lg p-6 text-sm text-content-2">Este reporte solo lo ven el dueño y los gerentes del negocio.</div>
        ) : (
          <>
            {rep && !rep.pro && <ProLocked feature="staff-report" compact />}

            <div className="bg-surface shadow rounded-lg p-4 sm:p-5 space-y-3 animate-fade-in-up">
              <div className="flex flex-wrap gap-1.5">
                {atajos.map((a) => {
                  const activo = a.from === from && a.to === to;
                  return (
                    <button key={a.k} type="button" onClick={() => { setFrom(a.from); setTo(a.to); }}
                      className={`rounded-full border px-3 py-1 text-xs ${activo ? 'border-accent bg-accent/10 text-accent' : 'border-line text-content-2 hover:bg-surface-2'}`}>
                      {a.label}
                    </button>
                  );
                })}
              </div>
              <div className={`grid grid-cols-2 gap-3 ${branches.length >= 2 ? 'lg:grid-cols-3' : ''} lg:max-w-3xl`}>
                <div>
                  <label className="mb-1 block text-xs font-medium text-content-2">Desde</label>
                  <input type="date" value={from} max={to || hoy} onChange={(e) => setFrom(e.target.value)} className={input} data-testid="staff-desde" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-content-2">Hasta</label>
                  <input type="date" value={to} min={from} max={hoy} onChange={(e) => setTo(e.target.value)} className={input} data-testid="staff-hasta" />
                </div>
                {branches.length >= 2 && (
                  <div className="col-span-2 lg:col-span-1">
                    <label className="mb-1 flex items-center gap-1 text-xs font-medium text-content-2"><MapPin size={12} /> Sucursal</label>
                    <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={input} data-testid="staff-sucursal">
                      <option value="">Todas</option>
                      {branches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400" data-testid="staff-error">{error}</p>}

            {loading && !rep ? (
              <div className="py-12 flex justify-center text-content-3"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : rep && (
              <div className={`space-y-4 ${loading ? 'opacity-60' : ''}`}>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                  {tiles.map((t) => (
                    <div key={t.k} className="bg-surface shadow rounded-lg px-4 py-3">
                      <p className="text-xs text-content-3">{t.k}</p>
                      <p className="text-lg font-semibold text-content tabular-nums">{t.v}</p>
                      {t.sub && <p className="text-xs text-content-2 tabular-nums">{t.sub}</p>}
                    </div>
                  ))}
                </div>

                {rep.empleados.length === 0 ? (
                  <div className="bg-surface shadow rounded-lg py-12 text-center text-sm text-content-3">
                    {rep.pro ? 'Sin movimientos en estas fechas.' : 'Aún no registras movimientos en estas fechas.'}
                  </div>
                ) : (
                  <>
                    {/* Escritorio: tabla con fila desplegable por día */}
                    <div className="hidden md:block bg-surface shadow rounded-lg overflow-x-auto">
                      <table className="w-full text-sm text-content" data-testid="staff-tabla">
                        <thead className="bg-surface-2 text-xs text-content-2">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-semibold">Empleado</th>
                            <th className="px-3 py-2.5 text-right font-semibold">Clientes nuevos</th>
                            <th className="px-3 py-2.5 text-right font-semibold">{lVisitas}</th>
                            <th className="px-3 py-2.5 text-right font-semibold">Monto</th>
                            <th className="px-3 py-2.5 text-right font-semibold">{lOtorgado}</th>
                            <th className="px-3 py-2.5 text-right font-semibold">Canjes · {cb ? 'saldo' : 'puntos'}</th>
                            <th className="px-3 py-2.5 text-right font-semibold">Ajustes</th>
                            <th className="px-3 py-2.5 text-right font-semibold">Cancel.</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {rep.empleados.map((e) => {
                            const abierto = !!abiertos[e.key];
                            return (
                              <React.Fragment key={e.key}>
                                <tr className="hover:bg-surface-2 cursor-pointer" onClick={() => setAbiertos((s) => ({ ...s, [e.key]: !abierto }))}>
                                  <td className="px-3 py-2.5 font-medium">
                                    <span className="inline-flex items-center gap-1.5">
                                      {abierto ? <ChevronDown size={14} className="text-content-3" /> : <ChevronRight size={14} className="text-content-3" />}
                                      <span className={e.automatico ? 'italic text-content-2' : ''}>{e.nombre}</span>
                                      <span className="text-[11px] font-normal text-content-3">{e.porDia.length} {e.porDia.length === 1 ? 'día' : 'días'}</span>
                                    </span>
                                  </td>
                                  {celdas(e.totales)}
                                </tr>
                                {abierto && e.porDia.map((d) => (
                                  <tr key={d.dia} className="bg-surface-2/50 text-content-2">
                                    <td className="pl-9 pr-3 py-2 text-xs">{fmtDia(d.dia)}</td>
                                    {celdas(d)}
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot className="border-t-2 border-line font-semibold">
                          <tr>
                            <td className="px-3 py-2.5">Total</td>
                            {celdas(rep.totales)}
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Móvil: una tarjeta por empleado, desplegable por día */}
                    <div className="md:hidden space-y-3" data-testid="staff-tarjetas">
                      {rep.empleados.map((e) => {
                        const abierto = !!abiertos[e.key];
                        return (
                          <div key={e.key} className="bg-surface shadow rounded-lg overflow-hidden">
                            <button type="button" onClick={() => setAbiertos((s) => ({ ...s, [e.key]: !abierto }))}
                              className="w-full flex items-center justify-between gap-2 px-4 pt-3 pb-2 text-left">
                              <span className={`font-semibold text-content truncate ${e.automatico ? 'italic' : ''}`}>{e.nombre}</span>
                              <span className="shrink-0 inline-flex items-center gap-1 text-xs text-content-3">
                                {e.porDia.length} {e.porDia.length === 1 ? 'día' : 'días'} {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </span>
                            </button>
                            <div className="px-4 pb-3">{rejilla(e.totales)}</div>
                            {abierto && (
                              <div className="border-t border-line divide-y divide-line bg-surface-2/50">
                                {e.porDia.map((d) => (
                                  <div key={d.dia} className="px-4 py-2.5">
                                    <p className="text-xs font-semibold text-content-2 mb-1">{fmtDia(d.dia)}</p>
                                    {rejilla(d)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                <p className="flex items-start gap-1.5 text-xs text-content-3">
                  <Info size={14} className="shrink-0 mt-0.5" />
                  <span>
                    Hora local del negocio ({rep.tz}). «Clientes nuevos» = su primer movimiento lo registró esa persona ese día.
                    Lo cancelado no suma en compras ni canjes: se cuenta en «Cancel.», a nombre de quien lo había registrado.
                    «Automático» = cumpleaños, referidos y movimientos del sistema.
                    {!rep.pro && ' Sin PRO solo ves lo que registraste tú.'}
                  </span>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default StaffReportPage;
